import React, { useEffect, useState } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { Clock, CheckCircle2, X, KeyRound, Star } from 'lucide-react';
import { useStore } from '../lib/store';
import { Input } from '../components/ui/Input';
export function CatchesPage({
  onNavigate
}: NavigationProps) {
  const {
    reservations,
    events,
    redeemReservation
  } = useStore();
  const [activeRedemptionId, setActiveRedemptionId] = useState<string | null>(null);
  const [redemptionStep, setRedemptionStep] = useState<'confirm' | 'exchange' | 'success'>('confirm');
  const [inputCode, setInputCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(7200);
  // Timer logic
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (activeRedemptionId && redemptionStep === 'exchange') {
      timer = setInterval(() => {
        setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [activeRedemptionId, redemptionStep]);
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const startRedemption = (id: string) => {
    setActiveRedemptionId(id);
    setRedemptionStep('confirm');
  };
  const confirmRedemption = () => {
    setRedemptionStep('exchange');
  };
  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const reservation = reservations.find(r => r.id === activeRedemptionId);
    if (reservation && inputCode.toUpperCase() === reservation.staffCode) {
      redeemReservation(reservation.id);
      setRedemptionStep('success');
    } else {
      alert('Incorrect code. Please ask the staff again.');
      setInputCode('');
    }
  };
  const closeRedemption = () => {
    setActiveRedemptionId(null);
    setRedemptionStep('confirm');
    setInputCode('');
  };
  const activeReservation = reservations.find(r => r.id === activeRedemptionId);
  return <div className="min-h-screen bg-gray-50 pb-24" data-id="element-276">
      <div className="px-6 pt-12 pb-6 bg-white shadow-sm sticky top-0 z-10" data-id="element-277">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-278">My Catches</h1>
        <div className="flex space-x-2 mt-4 overflow-x-auto pb-2 scrollbar-hide" data-id="element-279">
          {['All', 'Reserved', 'Redeemed'].map(filter => <button key={filter} className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs font-bold text-gray-600 transition-colors" data-id="element-280">
              {filter}
            </button>)}
        </div>
      </div>

      <div className="p-4 space-y-4" data-id="element-281">
        {reservations.length === 0 ? <div className="text-center py-16 px-4" data-id="element-282">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-283">
              <span className="text-4xl" data-id="element-284">🎣</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-285">
              No catches yet
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-286">
              Check the feed to find upcoming drops near you.
            </p>
            <Button variant="secondary" onClick={() => onNavigate('feed')} data-id="element-287">
              Find Drops
            </Button>
          </div> : reservations.map(res => {
        const event = events.find(e => e.id === res.eventId);
        if (!event) return null;
        return <div key={res.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 transition-all hover:shadow-md" data-id="element-288">
                <div className="flex justify-between items-start mb-4" data-id="element-289">
                  <div data-id="element-290">
                    <span className={`text-[10px] font-black px-2 py-1 rounded uppercase tracking-wider ${res.status === 'redeemed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-291">
                      {res.status}
                    </span>
                    <h3 className="font-bold text-gray-900 text-lg mt-2 leading-tight" data-id="element-292">
                      {event.brand}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-293">{event.title}</p>
                  </div>
                </div>

                {res.status === 'reserved' && <Button onClick={() => startRedemption(res.id)} className="w-full shadow-lg shadow-blue-500/20" data-id="element-294">
                    <KeyRound className="w-4 h-4 mr-2" data-id="element-295" />
                    Redeem Now
                  </Button>}

                {res.status === 'redeemed' && <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-id="element-296">
                    <p className="text-sm font-bold text-yellow-800 mb-3" data-id="element-297">
                      Review required to unlock next drop
                    </p>
                    <Button onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-298">
                      <Star className="w-4 h-4 mr-2" data-id="element-299" />
                      Review Box (4 items)
                    </Button>
                  </div>}

                {res.status === 'reviewed' && <div className="text-center text-gray-400 text-sm font-medium py-2" data-id="element-300">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" data-id="element-301" />
                    Review Completed
                  </div>}
              </div>;
      })}
      </div>

      {/* Redemption Modal Flow */}
      {activeReservation && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-302">
          <div className="bg-white rounded-3xl w-full max-w-sm relative overflow-hidden" data-id="element-303">
            <button onClick={closeRedemption} className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10" data-id="element-304">
              <X className="w-5 h-5 text-gray-600" data-id="element-305" />
            </button>

            {/* Step 1: Confirmation */}
            {redemptionStep === 'confirm' && <div className="p-8 text-center" data-id="element-306">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-307">
                  <Clock className="w-8 h-8 text-yellow-600" data-id="element-308" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-309">
                  Ready to collect?
                </h2>
                <p className="text-gray-500 mb-8 leading-relaxed" data-id="element-310">
                  Only activate this when you are at the location.
                </p>
                <div className="space-y-3" data-id="element-311">
                  <Button onClick={confirmRedemption} className="w-full py-4 text-lg" data-id="element-312">
                    Yes, I'm here
                  </Button>
                  <Button variant="secondary" onClick={closeRedemption} className="w-full" data-id="element-313">
                    Not yet
                  </Button>
                </div>
              </div>}

            {/* Step 2: Code Exchange */}
            {redemptionStep === 'exchange' && <div className="p-8 text-center" data-id="element-314">
                <div className="bg-blue-50 rounded-2xl p-6 mb-8 border border-blue-100" data-id="element-315">
                  <p className="text-xs font-bold text-blue-500 uppercase tracking-widest mb-2" data-id="element-316">
                    Show this to staff
                  </p>
                  <div className="text-5xl font-black text-blue-900 tracking-widest font-mono" data-id="element-317">
                    {activeReservation.userCode}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-318">
                  <p className="text-sm text-gray-500" data-id="element-319">
                    Staff will give you a code in return. Enter it below:
                  </p>

                  <form onSubmit={handleCodeSubmit} className="space-y-4" data-id="element-320">
                    <Input placeholder="ENTER CODE" value={inputCode} onChange={e => setInputCode(e.target.value.toUpperCase())} className="text-center text-2xl tracking-widest font-mono h-14 uppercase" maxLength={4} autoFocus data-id="element-321" />
                    <Button type="submit" className="w-full bg-black hover:bg-gray-800" data-id="element-322">
                      Unlock Box
                    </Button>
                  </form>
                </div>
              </div>}

            {/* Step 3: Success */}
            {redemptionStep === 'success' && <div className="p-8 text-center bg-green-50" data-id="element-323">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300" data-id="element-324">
                  <CheckCircle2 className="w-12 h-12 text-green-600" data-id="element-325" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-326">
                  Catch Secured!
                </h2>
                <p className="text-gray-600 mb-8" data-id="element-327">
                  You've successfully collected your drop.
                </p>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100 mb-8" data-id="element-328">
                  <p className="text-sm font-bold text-gray-900" data-id="element-329">
                    +20 Points Earned
                  </p>
                </div>
                <Button onClick={closeRedemption} className="w-full bg-green-600 hover:bg-green-700 text-white border-none" data-id="element-330">
                  Awesome!
                </Button>
              </div>}
          </div>
        </div>}
    </div>;
}